"use client";

import { useRouter } from "@/i18n/navigation";
import { useEffect, useRef } from "react";
import { useEventForm } from "./event-form-context";
import { GiveawayEventWithUserStatus } from "./giveaway/types";
import { WebinarEventWithUserStatus } from "./webinar/types";

interface EventLinkAutoRefreshProps {
    webinarEvents: WebinarEventWithUserStatus[];
    giveawayEvents: GiveawayEventWithUserStatus[];
}

const GRACE_PERIOD_MS = 2 * 1000; // 2 seconds

type RefreshPoint = {
    eventId: number;
    eventTitle: string;
    eventType: "webinar" | "giveaway";
    activationTime: number;
    reason: "link activation" | "event start";
};

/**
 * Client component that automatically refreshes the page when:
 * 1. Webinar event: meeting link becomes available (linkActiveDate + grace period)
 * 2. Webinar event: event officially starts (date + grace period)
 * 3. Giveaway event: registrations close and event becomes active (date + grace period)
 *
 * Tracks all upcoming refresh points and refreshes when the nearest one is reached.
 */
export function EventLinkAutoRefresh({
    webinarEvents,
    giveawayEvents,
}: EventLinkAutoRefreshProps) {
    const router = useRouter();
    const timeoutRef = useRef<NodeJS.Timeout | null>(null);
    const { isAnyModalOpen } = useEventForm();

    useEffect(() => {
        // Clear any existing timeout
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
        }

        const now = Date.now();
        const refreshPoints: RefreshPoint[] = [];

        // Calculate refresh points for webinar events
        webinarEvents.forEach((event) => {
            // Refresh when meeting link becomes available
            if (event.linkActiveDate) {
                const activationTime = new Date(event.linkActiveDate).getTime();
                const activationWithGrace = activationTime + GRACE_PERIOD_MS;
                if (activationWithGrace > now) {
                    refreshPoints.push({
                        eventId: event.id,
                        eventTitle: event.title,
                        eventType: "webinar",
                        activationTime: activationWithGrace,
                        reason: "link activation",
                    });
                }
            }

            // Refresh when webinar starts
            const eventStartTime = new Date(event.date).getTime();
            const startWithGrace = eventStartTime + GRACE_PERIOD_MS;
            if (startWithGrace > now) {
                refreshPoints.push({
                    eventId: event.id,
                    eventTitle: event.title,
                    eventType: "webinar",
                    activationTime: startWithGrace,
                    reason: "event start",
                });
            }
        });

        // Calculate refresh points for giveaway events
        giveawayEvents.forEach((event) => {
            // Refresh when giveaway becomes active (registrations close)
            const eventStartTime = new Date(event.date).getTime();
            const startWithGrace = eventStartTime + GRACE_PERIOD_MS;
            if (startWithGrace > now) {
                refreshPoints.push({
                    eventId: event.id,
                    eventTitle: event.title,
                    eventType: "giveaway",
                    activationTime: startWithGrace,
                    reason: "event start",
                });
            }
        });

        // Sort all refresh points by activation time
        refreshPoints.sort((a, b) => a.activationTime - b.activationTime);

        // If there are no upcoming refresh points, nothing to do
        if (refreshPoints.length === 0) {
            return;
        }

        // Get the nearest refresh point
        const nextRefreshPoint = refreshPoints[0];
        const timeUntilRefresh = nextRefreshPoint.activationTime - now;

        // Set timeout to refresh the page
        timeoutRef.current = setTimeout(() => {
            // Check if any modal is currently open before refreshing
            if (isAnyModalOpen) {
                console.log(
                    `[EventLinkAutoRefresh] Skipping refresh - registration modal is open`,
                );
                return;
            }

            console.log(
                `[EventLinkAutoRefresh] Refreshing page for ${nextRefreshPoint.eventType} ${nextRefreshPoint.reason}: ${nextRefreshPoint.eventTitle}`,
            );
            router.refresh();
        }, timeUntilRefresh);

        console.log(
            `[EventLinkAutoRefresh] Scheduled refresh in ${Math.round(timeUntilRefresh / 1000)}s for ${nextRefreshPoint.eventType} ${nextRefreshPoint.reason}: ${nextRefreshPoint.eventTitle}`,
        );

        // Cleanup on unmount or when dependencies change
        return () => {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }
        };
    }, [webinarEvents, giveawayEvents, router, isAnyModalOpen]);

    // This component doesn't render anything
    return null;
}
